import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ArrowUp, Brain, Check, Ellipsis, History, Loader2, Plus,
    Sparkles, Square, SquareMousePointer, X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Bubble, BubbleContent } from '@/components/ui/bubble';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import {
    CsAgentRun,
    CsAgentRunStreamEvent,
    CsAgentThread,
    CsRecipe,
} from '../../../../common/models/content-studio.model';
import { CanvasNode } from '../canvas/canvas-graph.types';
import { mentionQueryAt, mentionsPresent } from './mentions';
import { MentionComposer, MentionComposerHandle } from './MentionComposer';
import { markLatestReferences } from './thread-references';
import { availableRecipeSlugs } from '../canvas/RecipeSelector';
import {
    ActionTrail,
    AgentStatus,
    ChoiceChip,
    FeedbackControls,
    PendingPlanBar,
    planActions,
    PlanCard,
    ThoughtDisclosure,
    Turn,
    TurnResults,
} from './AgentTurnCards';
import { signalRunStarted } from '../../work/agent-work.store';
import {
    CanvasEditInfo,
    CanvasGenerationInfo,
    CompiledPlan,
    compilePlan,
    nodesAwaited,
    ContentPlan,
    ExistingNode,
    isEmptyPlan,
    PlannerProduct,
} from './plan-compiler';

/** Something on the canvas the next message is about. */
export interface AgentTarget {
    nodeId: string;
    kind: 'recipe' | 'generation' | 'edit';
    label: string;
    thumbnailUrl?: string;
}

export interface PlannerPanelProps {
    projectUuid: string;
    recipes: CsRecipe[];
    /** The live graph — deletion cascades and the canvas listing come from it. */
    canvasNodes: CanvasNode[];
    loadProducts: (uuids: string[]) => Promise<PlannerProduct[]>;
    existingNodes: Map<string, ExistingNode>;
    /** Generated images and videos on the canvas, so the agent can act on them. */
    generations: CanvasGenerationInfo[];
    /** Image-editing nodes chained onto generations. */
    edits: CanvasEditInfo[];
    mentionableProducts?: PlannerProduct[];
    onApply: (compiled: CompiledPlan, summary: string, generate: boolean) => void;
    /** Open a generated image full-size, the way clicking its node does. */
    onOpenGeneration?: (generationNodeId: string) => void;
    /**
     * Run a failed generation again. The canvas owns the job; this is so the
     * vendor can act on a failure where they were told about it.
     */
    onRetryGeneration?: (generationNodeId: string) => void;
    /** Let the vendor point at a finished image on the canvas. Resolves with
     *  its node id, or null if they backed out. */
    onPickGeneration?: () => Promise<string | null>;
    /**
     * A request handed in from the canvas — "Use AI" on a recipe card.
     *
     * Written into the composer rather than sent: the vendor sees what is
     * about to be asked and can change it, which is the whole difference
     * between this and the silent director it replaces.
     */
    /**
     * Work handed over from elsewhere — the dashboard's assistant, or an
     * insight card. `referenceUrl` is a picture the vendor already sent,
     * already hosted, so it is attached rather than asked for again.
     */
    handoff?: {
        id: string;
        text: string;
        referenceUrl?: string;
        /**
         * A node the vendor pointed at from the canvas, via its agent button.
         *
         * Not a message: pointing says WHICH thing, and they still have to say
         * what to do with it. So this adds a chip and focuses the box, and
         * never writes words they did not type.
         */
        target?: AgentTarget;
    } | null;
    onHandoffConsumed?: () => void;
    creditBalance?: number | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    collapsedHidden?: boolean;
    /**
     * How wide it is now, so the canvas can keep its own controls clear of it.
     * Fired while dragging, not only when the drag ends.
     */
    onWidthChange?: (width: number) => void;
}

/** Tabs beyond this crowd out the conversation; the rest live in history. */
const MAX_TABS = 4;

/**
 * A plan's second half, waiting on its first.
 *
 * A run ends when the plan is proposed, so "make a week of posts, then pick
 * the three best" could only ever be answered halfway — the rest is about
 * images that do not exist yet. The agent arms one of these instead of
 * guessing.
 *
 * The waiting used to happen here, keyed in localStorage, which made it a
 * promise only the tab that armed it could keep and left the panel and the plan
 * compiler each holding their own idea of when it was due. It now lives on the
 * server; the panel only shows that something is still coming, and picks up the
 * run when it starts.
 */

/** How this vendor likes to work — a preference, not a per-canvas setting. */
const AUTONOMY_KEY = 'ardealy.cs.autonomy';

/**
 * How wide the panel sits.
 *
 * Exported because the canvas has to keep its own controls clear of it — they
 * were underneath it whenever it was open.
 */
export const AGENT_PANEL_WIDTH = 380;

/** The gap it floats in, on every side. */
export const AGENT_PANEL_MARGIN = 12;

/**
 * How much wider than its default the vendor may drag it.
 *
 * A share of the window rather than a fixed number of pixels: the point of
 * widening is to see more of a plan without losing the canvas, and how much
 * canvas that costs depends on the screen. Past this the panel stops being a
 * side panel.
 */
const AGENT_PANEL_MAX_EXTRA = 0.18;

/** Where the vendor left it. A working preference, not a per-canvas setting. */
const PANEL_WIDTH_KEY = 'ardealy.cs.panelWidth';

/**
 * Which conversation to reopen a project in.
 *
 * The server records it: opening a thread stamps `lastOpenedAt` on it, so this
 * follows the account rather than the browser it was last read in. Falls back
 * to the most recently used, which is right for a project opened here for the
 * first time.
 */
function threadToResume(threads: CsAgentThread[]): CsAgentThread | undefined {
    const read = threads
        .filter((th) => th.lastOpenedAt)
        .sort((a, b) => Date.parse(b.lastOpenedAt!) - Date.parse(a.lastOpenedAt!));
    return read[0] || threads[0];
}

/**
 * Whether this project had a conversation last time the panel looked.
 *
 * A hint, not the answer — the server owns which thread, and this exists only
 * so a project with history opens straight into it instead of showing the
 * bottom bar for a beat first. Wrong at worst by one render.
 */
const historyHintKey = (projectUuid: string) => `ardealy.cs.hasChats.${projectUuid}`;

const rememberHasHistory = (projectUuid: string, has: boolean) => {
    try {
        if (has) window.localStorage.setItem(historyHintKey(projectUuid), '1');
        else window.localStorage.removeItem(historyHintKey(projectUuid));
    } catch {
        // Private mode: the panel opens a beat later, which is the whole cost.
    }
};

export function hasAgentHistory(projectUuid: string): boolean {
    try {
        return !!window.localStorage.getItem(historyHintKey(projectUuid));
    } catch {
        return false;
    }
}

/** The widest it may be drawn right now. */
export function maxPanelWidth(viewport: number): number {
    return Math.round(AGENT_PANEL_WIDTH + viewport * AGENT_PANEL_MAX_EXTRA);
}

/**
 * Tokens per displayed credit. A presentation rate so agent spend reads in the
 * same unit as generation spend — not a billing rule; nothing is debited.
 */
const TOKENS_PER_CREDIT = 250;

/**
 * The Studio Agent panel.
 *
 * A set of conversations, each a persisted thread of durable runs. Tabs across
 * the top are the open ones; the clock opens everything ever started. Because
 * the state lives server-side, switching tabs, collapsing the panel or
 * reloading the page all restore rather than reset.
 */
export const PlannerPanel: React.FC<PlannerPanelProps> = ({
    projectUuid,
    recipes,
    canvasNodes,
    loadProducts,
    existingNodes,
    generations,
    edits,
    mentionableProducts = [],
    onApply,
    onOpenGeneration,
    onRetryGeneration,
    onPickGeneration,
    handoff,
    onHandoffConsumed,
    creditBalance,
    open,
    onOpenChange,
    collapsedHidden = false,
    onWidthChange,
}) => {
    const { t } = useTranslation();
    const api = useContentStudioApi();

    const [threads, setThreads] = useState<CsAgentThread[]>([]);
    const [openTabs, setOpenTabs] = useState<string[]>([]);
    const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
    /**
     * How wide the panel is, and the drag that changes it.
     *
     * Clamped on the way in as well as during the drag: a stored width from a
     * wider monitor would otherwise reopen the panel across most of a laptop
     * screen, with no obvious way to discover the handle that shrinks it.
     */
    const [panelWidth, setPanelWidth] = useState(() => {
        const stored = Number(window.localStorage.getItem(PANEL_WIDTH_KEY));
        if (!Number.isFinite(stored) || stored <= 0) return AGENT_PANEL_WIDTH;
        return Math.min(Math.max(stored, AGENT_PANEL_WIDTH), maxPanelWidth(window.innerWidth));
    });
    const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

    useEffect(() => { onWidthChange?.(panelWidth); }, [panelWidth, onWidthChange]);

    const startResize = useCallback((event: React.MouseEvent) => {
        event.preventDefault();
        dragRef.current = { startX: event.clientX, startWidth: panelWidth };

        const onMove = (e: MouseEvent) => {
            const drag = dragRef.current;
            if (!drag) return;
            // Dragging left widens: the panel is anchored to the right edge.
            const next = drag.startWidth + (drag.startX - e.clientX);
            setPanelWidth(Math.min(
                Math.max(next, AGENT_PANEL_WIDTH),
                maxPanelWidth(window.innerWidth),
            ));
        };
        const onUp = () => {
            dragRef.current = null;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };

        // The pointer leaves the handle the moment the drag starts, so the
        // cursor and the text selection are held at the document level.
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [panelWidth]);

    useEffect(() => {
        try { window.localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth)); } catch { /* private mode */ }
    }, [panelWidth]);

    // A window that shrinks must not leave the panel wider than it may be.
    useEffect(() => {
        const onResize = () => setPanelWidth(
            (w) => Math.min(w, maxPanelWidth(window.innerWidth)),
        );
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    /**
     * Generations being run again on the vendor's say-so.
     *
     * Cleared when the node stops being failed — whether it came back or failed
     * differently — so "Trying again…" never outlives the attempt.
     */
    const [retryingGenerations, setRetryingGenerations] = useState<Set<string>>(new Set());

    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyQuery, setHistoryQuery] = useState('');

    const [turns, setTurns] = useState<Turn[]>([]);
    /**
     * The conversation as it stands, readable from a timer.
     *
     * The poll that picks up a server-started run cannot close over `turns` —
     * it would compare against whatever the list was when the interval began.
     */
    const turnsRef = useRef<Turn[]>([]);
    /**
     * Runs in flight, with the conversation each belongs to.
     *
     * "Is the agent working" used to be one boolean for the whole panel, and
     * switching conversations aborted the stream and cleared it — so a turn
     * left running in one chat was killed by looking at another, and coming
     * back showed it as an error. A run belongs to its thread; the panel is
     * just a window onto one of them.
     */
    const [activeRuns, setActiveRuns] = useState<Array<{
        runId: string; threadId: string | null; turnId: string;
    }>>([]);
    const running = activeRuns.some((r) => (r.threadId ?? null) === (activeThreadId ?? null));
    /**
     * Bumped every time the vendor changes which conversation is on screen.
     *
     * A run started in a brand-new chat only learns its thread id when the
     * server answers. If the vendor has moved on by then, that late answer must
     * not drag the view back — it only files the run under the right thread.
     */
    const viewEpochRef = useRef(0);
    /**
     * Turns the vendor stopped before the server had answered with a run id.
     * Without this, Stop pressed during that window removed the placeholder and
     * the run it could not name yet carried on to completion regardless.
     */
    const cancelledTurnsRef = useRef<Set<string>>(new Set());
    const [message, setMessage] = useState('');
    const [threadTokens, setThreadTokens] = useState(0);
    /**
     * Whether the model reasons before answering. Off by default: it costs
     * tokens and latency, and most turns are "add a shot of the sofa".
     */
    const [thinking, setThinking] = useState(false);

    /**
     * Whether a finished plan waits for approval or just happens.
     *
     * `ask` is the default and stays the default: a plan spends credits, and a
     * vendor who has not seen one before should get to read it first. `auto` is
     * for once they trust it — the work they described starts without a button
     * in the way. Remembered per vendor, not per project, because it is a
     * preference about working style rather than about a canvas.
     */
    const [autonomy, setAutonomy] = useState<'ask' | 'auto'>(() => {
        try {
            return window.localStorage.getItem(AUTONOMY_KEY) === 'auto' ? 'auto' : 'ask';
        } catch { return 'ask'; }
    });
    const [autonomyOpen, setAutonomyOpen] = useState(false);
    /** The menu and its button together, so a click on either is "inside". */
    const autonomyMenuRef = useRef<HTMLDivElement | null>(null);
    const chooseAutonomy = useCallback((next: 'ask' | 'auto') => {
        setAutonomy(next);
        setAutonomyOpen(false);
        try { window.localStorage.setItem(AUTONOMY_KEY, next); } catch { /* private mode */ }
    }, []);

    const [mentioned, setMentioned] = useState<PlannerProduct[]>([]);
    /**
     * Images the vendor pointed at for this message.
     *
     * "This one" is how people talk about their own pictures, and the agent had
     * no way to be shown which — it could only guess from an unordered listing.
     * Held as node ids and resolved against the live canvas at send time, so a
     * picture deleted in the meantime simply drops out.
     */
    const [mentionedImages, setMentionedImages] = useState<string[]>([]);
    /** Nodes pointed at from the canvas — recipes, edits and images alike. */
    const [targets, setTargets] = useState<AgentTarget[]>([]);
    /**
     * Pictures the vendor attached as inspiration — uploaded, or fetched from a
     * pasted address. Ids are minted here (`ref-N`) so the agent can name one to
     * `look_at_images` the same way it names a generation node.
     *
     * Kept for the whole conversation, not cleared on send: the agent may ask
     * what to take from the picture, and the answer arrives a turn later — the
     * picture has to still be there.
     */
    const [referenceImages, setReferenceImages] = useState<Array<{
        id: string; url: string; name?: string; sourceUrl?: string; uploading?: boolean;
    }>>([]);
    /**
     * Reference ids, unique for the life of the thread and not just the page.
     *
     * A plain counter restarts at 1 on every reload, so two different pictures
     * attached either side of one are both `ref-1` — and the agent's history,
     * the `attachedNow` mark and `read_reference_camera` all key off that id.
     * The suffix costs nothing and removes a whole class of "it used the wrong
     * picture" that is impossible to see from a transcript.
     */
    const referenceSeq = useRef(0);
    const referenceRunId = useRef(Math.random().toString(36).slice(2, 7));
    const nextReferenceId = useCallback(() => {
        referenceSeq.current += 1;
        return `ref-${referenceRunId.current}-${referenceSeq.current}`;
    }, []);
    /**
     * Everything attached in this conversation, sent with every turn. The
     * composer chips clear on send — visually the picture belongs to the
     * message that carried it — but the agent may only ask what to take from
     * it a turn later, so the context must keep carrying it.
     */
    const threadReferencesRef = useRef<Array<{ id: string; url: string; name?: string; sourceUrl?: string }>>([]);
    const [plusMenuOpen, setPlusMenuOpen] = useState(false);
    // A request handed over from a recipe card. Keyed by id so the same card
    // asked twice arrives twice, rather than being swallowed as "unchanged".
    const handoffId = useRef<string | null>(null);
    const [refUrlOpen, setRefUrlOpen] = useState(false);
    const [refUrlValue, setRefUrlValue] = useState('');
    const [refUrlBusy, setRefUrlBusy] = useState(false);
    const [refError, setRefError] = useState<string | null>(null);
    const refFileInputRef = useRef<HTMLInputElement | null>(null);
    /**
     * The turn whose question is being answered in the vendor's own words.
     *
     * A question with two options is a question with two options: when neither
     * is what they meant, "No, I meant something different" throws the answer
     * away and asks again. Typing into the composer always worked, but nothing
     * on screen said so.
     */
    const [customAnswerFor, setCustomAnswerFor] = useState<string | null>(null);
    const [customAnswer, setCustomAnswer] = useState('');
    const [mentionQuery, setMentionQuery] = useState<string | null>(null);
    const [mentionIndex, setMentionIndex] = useState(0);

    /**
     * Runs this session already applied on the vendor's behalf.
     *
     * Belt and braces with the server's `applied` flag: marking it is a
     * best-effort call, and reopening the panel must not build the same nodes
     * twice if that call was the thing that failed.
     */
    const autoAppliedRef = useRef<Set<string>>(new Set());

    /** Open streams by runId. Kept per run so switching conversations does not
     *  cut one off; all are closed when the panel unmounts. */
    const streamsRef = useRef<Map<string, AbortController>>(new Map());
    const runIdRef = useRef<string | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const composerRef = useRef<MentionComposerHandle | null>(null);
    const mentionRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => () => {
        streamsRef.current.forEach((c) => c.abort());
        streamsRef.current.clear();
    }, []);

    // Read inside stream callbacks, which close over the values they were
    // created with — a mode changed mid-run would otherwise not be seen.
    const autonomyRef = useRef(autonomy);
    useEffect(() => { autonomyRef.current = autonomy; }, [autonomy]);
    const generationsRef = useRef(generations);
    useEffect(() => { generationsRef.current = generations; }, [generations]);
    useEffect(() => { turnsRef.current = turns; }, [turns]);

    /** A run has ended — stop counting it as in flight and close its stream. */
    const finishRun = useCallback((runId: string) => {
        streamsRef.current.get(runId)?.abort();
        streamsRef.current.delete(runId);
        setActiveRuns((prev) => prev.filter((r) => r.runId !== runId));
    }, []);

    /**
     * Pick a run back up that was already going when this view opened.
     *
     * Reopening a conversation whose turn is still working should show it
     * working. The stream may already be attached — from before the vendor
     * switched away — in which case there is nothing to do but count it.
     */
    const resumeRunRef = useRef<(runId: string, threadId: string, turnId: string) => void>(() => {});
    const resumeRun = useCallback((runId: string, threadId: string, turnId: string) => {
        resumeRunRef.current(runId, threadId, turnId);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [turns, running]);

    // ── Continuations ────────────────────────────────────────────
    // A plan whose second half depends on its first: "make a week of posts,
    // then pick the best three". Stored rather than held in memory because the
    // wait is minutes long and the vendor has no reason to sit and watch it —
    // closing the panel, or the tab, should not lose the follow-up.
    /**
     * What this project is waiting to follow up on, as the server sees it.
     *
     * The waiting itself is no longer done here. This used to be a localStorage
     * key plus an effect that watched the canvas and fired the follow-up — so
     * the promise belonged to whichever tab made it, and the rule for when it
     * was due had to agree with the plan compiler's idea of the same thing.
     * Twice they disagreed and the second half of a job silently never ran.
     * Now the server watches the project's assets and starts the run; this is
     * only so the vendor can see that something is still coming.
     */
    const [continuation, setContinuation] = useState<{
        continuationId: string; instruction: string; threadId: string;
    } | null>(null);

    const refreshContinuation = useCallback(async () => {
        try {
            const armed = await api.listAgentContinuations(projectUuid);
            const first = armed?.[0];
            setContinuation(first
                ? {
                    continuationId: first.continuationId,
                    instruction: first.instruction,
                    threadId: first.threadId,
                }
                : null);
            return first || null;
        } catch {
            return null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectUuid]);

    useEffect(() => { void refreshContinuation(); }, [refreshContinuation]);

    // ── Threads ──────────────────────────────────────────────────
    const refreshThreads = useCallback(async () => {
        try {
            const list = await api.listAgentThreads(projectUuid);
            setThreads(list);
            return list;
        } catch {
            return [] as CsAgentThread[];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectUuid]);

    useEffect(() => {
        void refreshThreads().then((list) => {
            // Reopen the most recent conversations as tabs so the panel picks up
            // where the vendor left off rather than starting blank every visit.
            const recent = list.slice(0, MAX_TABS).map((thread) => thread.threadId);
            setOpenTabs(recent);

            rememberHasHistory(projectUuid, list.length > 0);
            if (list.length === 0) {
                // Nothing has ever been said here. Leave the panel as the bar at
                // the bottom rather than opening a pane onto an empty canvas.
                return;
            }

            const resume = threadToResume(list);
            if (!resume) return;

            setOpenTabs((prev) => (
                prev.includes(resume.threadId) ? prev : [resume.threadId, ...prev].slice(0, MAX_TABS)
            ));
            void loadThread(resume.threadId);
            // Opened here even if the hint said otherwise: a project talked to
            // on another machine still comes back to where it left off, one
            // beat later than one this browser already knew about.
            onOpenChange(true);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectUuid]);

    /** Rebuild the conversation view from persisted runs. */
    const loadThread = useCallback(async (threadId: string) => {
        // Streams are deliberately left alone: a run in another conversation
        // keeps going, and one in this conversation is re-attached below.
        viewEpochRef.current += 1;
        setActiveThreadId(threadId);
        setTurns([]);
        // References belong to the conversation they were attached in.
        setReferenceImages([]);
        threadReferencesRef.current = [];

        try {
            const { thread, runs } = await api.getAgentThread(threadId);
            setThreadTokens((thread.tokensIn || 0) + (thread.tokensOut || 0));

            const rebuilt: Turn[] = [];
            for (const run of runs as CsAgentRun[]) {
                const result: any = run.result;
                const thinkingEnabled = !!(run as any).thinking;
                const thoughts = thinkingEnabled
                    ? ((run as any).events
                        ?.filter((e: any) => e.type === 'thinking' && e.data?.message)
                        .map((e: any) => e.data.message) || [])
                    : [];

                // A run that is still going is still going. Rebuilt as an error
                // it told the vendor their turn had failed, when all they had
                // done was look at another conversation while it worked.
                const stillRunning = run.status === 'running' || run.status === 'pending';

                const turn: Turn = {
                    id: run.runId,
                    runId: run.runId,
                    message: run.message,
                    activity: [],
                    thoughts,
                    status: stillRunning ? 'running' : (run.status === 'succeeded' ? 'done' : 'error'),
                    error: (stillRunning || run.status === 'succeeded') ? undefined : run.error,
                    feedback: run.feedback,
                    applied: (run as any).applied,
                    thinkingEnabled,
                };

                if (stillRunning) {
                    resumeRun(run.runId, threadId, run.runId);
                    rebuilt.push(turn);
                    continue;
                }

                if (result?.question) {
                    turn.question = {
                        question: result.question,
                        options: result.options,
                        context: result.context,
                    };
                } else if (result?.reply) {
                    // Legacy prose ending — see the note in handleTerminal.
                    turn.summary = String(result.reply);
                } else if (Array.isArray(result?.operations)) {
                    turn.summary = result.summary;
                    // Re-compiled against the *current* canvas, so a plan whose
                    // target nodes are gone reports that rather than silently
                    // applying to whatever now holds those ids.
                    try {
                        const products = await loadProducts(result.productUuids || []);
                        const compiled = compilePlan(
                            result, new Map(products.map((p) => [p.uuid, p])),
                            recipes, { x: 0, y: 0 }, existingNodes, canvasNodes,
                        );
                        if (!isEmptyPlan(compiled)) {
                            turn.plan = compiled;

                            /**
                             * Already approved, somewhere else.
                             *
                             * A plan dispatched from the general assistant was
                             * approved there — by the vendor, on the brief. Left
                             * as a proposal it asks them to approve the same work
                             * a second time, in a panel they did not open, which
                             * is what they saw the first time this shipped.
                             *
                             * Applied AND generated. Approving in the assistant
                             * is approving the work, not approving a canvas: the
                             * first version applied without generating, and the
                             * vendor got three cards that had everything and
                             * produced nothing. The panel shows the credit
                             * estimate on the same card, so the cost is visible
                             * where the outcome is.
                             */
                            const approvedUpstream = (run as any).context?.approvedUpstream;
                            if (approvedUpstream && !turn.applied && !autoAppliedRef.current.has(run.runId)) {
                                // Guarded locally as well as by `applied` from the
                                // server: if marking it fails, reopening the panel
                                // must not build the same nodes again.
                                autoAppliedRef.current.add(run.runId);
                                onApply(compiled, result.summary || '', true);
                                turn.applied = true;
                                void api.markAgentRunApplied(run.runId).catch(() => undefined);
                            }
                        } else if (!turn.applied) {
                            // Nothing left to offer and it never landed, so the
                            // summary describes work that never happened. Once
                            // applied it is a true record and stays as written.
                            turn.status = 'error';
                            turn.summary = undefined;
                            turn.error = compiled.dropped[0]?.reason
                                || t('CONTENT_STUDIO.PLANNER.ERROR_NOT_COMPILABLE',
                                    'None of the planned steps could be built from your catalog.');
                        }
                    } catch {
                        // Leave the summary; the plan just can't be re-offered.
                    }
                }
                rebuilt.push(turn);
            }
            setTurns(rebuilt);
        } catch {
            setTurns([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, canvasNodes, existingNodes, loadProducts, onApply, recipes]);

    const openThread = useCallback((threadId: string) => {
        setOpenTabs((prev) => (prev.includes(threadId) ? prev : [threadId, ...prev].slice(0, MAX_TABS)));
        setHistoryOpen(false);
        void loadThread(threadId);
    }, [loadThread]);

    const closeTab = useCallback((threadId: string) => {
        setOpenTabs((prev) => {
            const next = prev.filter((id) => id !== threadId);
            if (threadId === activeThreadId) {
                // Fall to a neighbouring tab, or to a blank new conversation.
                const fallback = next[0];
                if (fallback) void loadThread(fallback);
                else { setActiveThreadId(null); setTurns([]); setThreadTokens(0); }
            }
            return next;
        });
        // The thread itself is closed server-side, not deleted — history keeps it.
        void api.closeAgentThread(threadId).then(refreshThreads).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeThreadId, loadThread, refreshThreads]);

    const newConversation = useCallback(() => {
        // Nothing is aborted: a run started in another conversation is that
        // conversation's, and starting a new one does not cancel it.
        viewEpochRef.current += 1;
        setActiveThreadId(null);
        setTurns([]);
        setThreadTokens(0);
        setMessage('');
        setReferenceImages([]);
        threadReferencesRef.current = [];
        setHistoryOpen(false);
        window.setTimeout(() => inputRef.current?.focus(), 0);
    }, []);

    /**
     * What the vendor could pick by hand, per product type. Sent with every run
     * so the planner cannot propose a recipe the picker hides — the rules live
     * in RecipeSelector and are read from there rather than restated.
     */
    const recipeAvailability = useMemo(() => ({
        surface: availableRecipeSlugs(recipes, 'surface'),
        model3d: availableRecipeSlugs(recipes, 'model3d'),
        image_only: availableRecipeSlugs(recipes, 'image_only'),
    }), [recipes]);

    // ── Mentions ─────────────────────────────────────────────────
    /** Products a query still names. One matcher, so the list the vendor sees
     *  and the test for "is this still a mention" can never disagree. */
    const productsMatching = useCallback((query: string): PlannerProduct[] => {
        const words = query.toLowerCase().split(/\s+/).filter(Boolean);
        const pool = mentionableProducts.filter((p) => !mentioned.some((m) => m.uuid === p.uuid));
        if (words.length === 0) return pool.slice(0, 20);
        return pool.filter((p) => {
            const hay = `${p.name || ''} ${(p as any).sku || ''}`.toLowerCase();
            return words.every((w) => hay.includes(w));
        }).slice(0, 20);
    }, [mentionableProducts, mentioned]);

    const mentionMatches = useMemo(
        () => (mentionQuery == null ? [] : productsMatching(mentionQuery)),
        [mentionQuery, productsMatching],
    );

    /**
     * Is the vendor still picking a product, or have they moved on?
     *
     * A name can contain spaces, so the query runs to the end of the text —
     * which meant that once a mention was accepted, every character typed after
     * it still read as "choosing a product". The picker stayed open over the
     * conversation and, worse, Enter picked from it instead of sending the
     * message. A name already accepted, followed by a space, is settled text.
     */
    const queryFromText = useCallback((value: string, caret?: number): string | null => (
        mentionQueryAt(value, caret ?? value.length, {
            matches: (q) => productsMatching(q).length > 0,
            accepted: mentioned.map((p) => p.name).filter(Boolean),
        })
    ), [mentioned, productsMatching]);

    const acceptMention = useCallback((product: PlannerProduct) => {
        /**
         * The editor writes the badge itself, at the caret.
         *
         * It owns the DOM, so it is the only thing that knows where the caret
         * is and which text node holds the "@query" being replaced. Rebuilding
         * the value from a string here would put the caret back at the end and
         * lose the rest of the sentence they were mid-way through.
         */
        composerRef.current?.insertMention({
            uuid: product.uuid, name: product.name || '', thumbnailPath: product.thumbnailPath,
        });
        setMentioned((prev) => (prev.some((p) => p.uuid === product.uuid) ? prev : [...prev, product]));
        setMentionQuery(null);
        setMentionIndex(0);
        composerRef.current?.focus();
    }, []);

    const removeMention = useCallback((product: PlannerProduct) => {
        setMentioned((prev) => prev.filter((p) => p.uuid !== product.uuid));
        setMessage((prev) => prev
            .replace(`@${product.name || ''}`, '')
            .replace(/\s{2,}/g, ' ')
            .trimStart());
    }, []);

    useEffect(() => {
        if (mentioned.length === 0) return;
        const present = mentioned.filter((p) => p.name && message.includes(p.name));
        if (present.length !== mentioned.length) setMentioned(present);
    }, [message, mentioned]);

    /**
     * A pasted mention is still a mention.
     *
     * Picking from the list is not the only way an "@Name" gets into the box —
     * a prompt written elsewhere and pasted in has one too, and it used to
     * arrive as plain text: no pill, and the product never reached the agent.
     * Longest name first, so one product whose name begins another cannot
     * shadow it.
     */
    useEffect(() => {
        const candidates = mentionableProducts.filter(
            (p) => p.name && !mentioned.some((m) => m.uuid === p.uuid),
        );
        const names = mentionsPresent(message, candidates.map((p) => p.name));
        const found = names
            .map((n) => candidates.find((p) => p.name === n))
            .filter(Boolean) as PlannerProduct[];
        if (found.length) setMentioned((prev) => [...prev, ...found]);
    }, [message, mentionableProducts, mentioned]);

    // Dismiss the product list on an outside click. Without this it stayed open
    // until something else happened to close it.
    useEffect(() => {
        if (mentionQuery == null) return;
        const onDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (mentionRef.current?.contains(target)) return;
            if (inputRef.current?.contains(target)) return;
            setMentionQuery(null);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [mentionQuery]);

    // The same for the Ask-first / Auto menu, which stayed open over the canvas
    // until it was clicked again — now that it is not clipped by the panel, an
    // open menu can sit over work the vendor is trying to look at.
    useEffect(() => {
        if (!autonomyOpen) return undefined;
        const onDown = (e: MouseEvent) => {
            if (autonomyMenuRef.current?.contains(e.target as Node)) return;
            setAutonomyOpen(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAutonomyOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [autonomyOpen]);

    // ── Turn helpers ─────────────────────────────────────────────
    const patchTurn = useCallback((turnId: string, patch: Partial<Turn>) => {
        setTurns((prev) => prev.map((turn) => (turn.id === turnId ? { ...turn, ...patch } : turn)));
    }, []);

    const appendTo = useCallback((turnId: string, key: 'activity' | 'thoughts', line: string) => {
        setTurns((prev) => prev.map((turn) => {
            if (turn.id !== turnId) return turn;
            const list = turn[key];
            if (list[list.length - 1] === line) return turn;
            return { ...turn, [key]: [...list, line] };
        }));
    }, []);

    const describeEvent = useCallback((event: CsAgentRunStreamEvent): string | null => {
        const d = event.data || {};
        if (event.type === 'tool_call') {
            switch (d.tool) {
                case 'search_products': return t('CONTENT_STUDIO.PLANNER.ACTIVITY_SEARCHING', 'Looking through your products…');
                case 'inspect_products': return t('CONTENT_STUDIO.PLANNER.ACTIVITY_INSPECTING', 'Studying how they look…');
                case 'list_recipes': return t('CONTENT_STUDIO.PLANNER.ACTIVITY_RECIPES', 'Checking what can be made…');
                case 'get_recipe_schema': return t('CONTENT_STUDIO.PLANNER.ACTIVITY_SCHEMA', 'Working out the settings…');
                case 'propose_plan': return t('CONTENT_STUDIO.PLANNER.ACTIVITY_DRAFTING', 'Drafting the plan…');
                default: return null;
            }
        }
        if (event.type === 'tool_result' && d.tool === 'search_products'
            && typeof d.result?.count === 'number') {
            return d.result.count > 0
                ? t('CONTENT_STUDIO.PLANNER.ACTIVITY_FOUND', {
                    defaultValue: 'Found {{count}} products', count: d.result.count,
                })
                : t('CONTENT_STUDIO.PLANNER.ACTIVITY_NONE_FOUND', 'No match — trying different words…');
        }
        return null;
    }, [t]);

    const handleTerminal = useCallback(async (turnId: string, data: Record<string, any>) => {
        void refreshThreads();

        if (data.status !== 'succeeded') {
            patchTurn(turnId, {
                status: 'error',
                error: data.error || t('CONTENT_STUDIO.PLANNER.ERROR_GENERIC',
                    "The planner couldn't finish. Try rephrasing."),
            });
            return;
        }

        const result = data.result as any;

        if (result?.question) {
            patchTurn(turnId, {
                status: 'done',
                question: { question: result.question, options: result.options, context: result.context },
            });
            return;
        }

        // Legacy: prose endings were briefly allowed. Kept so conversations
        // recorded then still render; nothing produces them now.
        if (result?.reply) {
            patchTurn(turnId, { status: 'done', summary: String(result.reply) });
            return;
        }

        if (!result || !Array.isArray(result.operations)) {
            patchTurn(turnId, {
                status: 'error',
                error: t('CONTENT_STUDIO.PLANNER.ERROR_NO_STEPS', 'Nothing to plan for that request.'),
            });
            return;
        }

        try {
            const products = await loadProducts(result.productUuids || []);
            const compiled = compilePlan(
                result as ContentPlan, new Map(products.map((p) => [p.uuid, p])),
                recipes, { x: 0, y: 0 }, existingNodes, canvasNodes,
            );

            if (isEmptyPlan(compiled)) {
                // The agent's summary describes work it intended to do. With
                // nothing to apply, showing that summary on its own reads as
                // "done" — the vendor is told the canvas changed when it did
                // not. Report the reason instead; the summary is not an outcome.
                patchTurn(turnId, {
                    status: 'error',
                    error: compiled.dropped[0]?.reason
                        || t('CONTENT_STUDIO.PLANNER.ERROR_NOT_COMPILABLE',
                            'None of the planned steps could be built from your catalog.'),
                });
                return;
            }
            patchTurn(turnId, { status: 'done', plan: compiled, summary: result.summary });
            /**
             * The exchange is over, so the target is spent.
             *
             * Clearing it on SEND was wrong: the agent often answers with a
             * question, and the reply to that question arrived carrying no
             * target at all — so a conversation that began "add furniture to
             * this image" ended up rewriting the recipe that made it, because
             * by then nothing said which image was meant. A plan is the point
             * at which the ask has been answered; a question is not.
             */
            setTargets([]);

            // In Auto the plan is the answer, not a proposal. Applied with
            // generate, because a vendor who turned the approval step off asked
            // for the work, not for a canvas full of nodes waiting on a button.
            if (autonomyRef.current === 'auto') {
                const before = generationsRef.current.map((g) => g.nodeId);
                onApply(compiled, result.summary || '', true);
                patchTurn(turnId, { applied: true, producedAfter: before });
                if (runIdRef.current) {
                    void api.markAgentRunApplied(runIdRef.current).catch(() => undefined);
                }
            }
        } catch (err: any) {
            patchTurn(turnId, { status: 'error', error: err?.message });
        }
    }, [api, canvasNodes, existingNodes, loadProducts, onApply, patchTurn, recipes,
        refreshThreads, t]);

    // Wired through a ref because resuming needs the handlers defined above it,
    // while loadThread — defined before them — is what discovers a live run.
    useEffect(() => {
        resumeRunRef.current = (runId, threadId, turnId) => {
            setActiveRuns((prev) => (prev.some((r) => r.runId === runId)
                ? prev
                : [...prev, { runId, threadId, turnId }]));
            if (streamsRef.current.has(runId)) return; // already listening
            streamsRef.current.set(runId, api.streamAgentRun(runId, (event) => {
                if (event.type === 'status') {
                    finishRun(runId);
                    void handleTerminal(turnId, event.data || {});
                    return;
                }
                if (event.type === 'error') {
                    finishRun(runId);
                    patchTurn(turnId, { status: 'error', error: event.data?.message || '' });
                    return;
                }
                const line = describeEvent(event);
                if (line) appendTo(turnId, 'activity', line);
            }));
        };
    }, [api, appendTo, describeEvent, finishRun, handleTerminal, patchTurn]);

    /**
     * What the agent is told about this canvas.
     *
     * Shared by the run that is being sent and the follow-up being armed: the
     * server keeps this snapshot and hands it to the callback, so a promise
     * made now is answered against the canvas as it was when the plan landed.
     * Built in one place because the last time the panel and the compiler each
     * had their own idea of the canvas, the second half of a job stopped
     * running and neither of them was obviously wrong.
     */
    const canvasContext = useCallback(() => ({
        recipeAvailability,
        canvas: {
            nodes: [...existingNodes.values()].map((n) => ({
                nodeId: n.nodeId,
                recipeSlug: n.recipeSlug,
                recipeName: n.recipeName,
                productUuid: n.product?.uuid,
                productName: n.product?.name,
                // Current settings, so "make it blue" edits from what the node
                // actually says rather than a guess.
                inputs: n.inputs,
                // The node's effective state, not just what is stored: a value
                // the current mode ignores looks perfectly editable in `inputs`
                // and is not.
                mode: n.mode,
                ignoredInputs: n.ignoredInputs?.length ? n.ignoredInputs : undefined,
                missing: n.missing?.length ? n.missing : undefined,
                hasOutput: n.hasOutput,
                // The frame the studio captured for this node. A run that asks
                // for an angle ends there without it — nothing tells the next
                // turn the shot was taken.
                snapshot: n.snapshot,
            })),
            // Everything else the vendor can point at. Without these, "accept
            // both" and "retry the failed one" name things the agent cannot see.
            generations: generations.length ? generations : undefined,
            edits: edits.length ? edits : undefined,
        },
    }), [edits, existingNodes, generations, recipeAvailability]);

    // ── Sending ──────────────────────────────────────────────────
    const send = useCallback(async (text: string, opts?: {
        /** Started by the canvas following through on a plan, not by the vendor. */
        auto?: boolean;
        /** Continue this thread specifically — an armed continuation belongs to
         *  the conversation that armed it, which may not be the one on screen. */
        threadId?: string;
        /**
         * They answered by clicking, not typing. Shown as a chip beside the
         * agent's work rather than quoted back as a message in their voice.
         */
        choice?: { label: string; thumbnailUrl?: string };
    }) => {
        const prompt = text.trim();
        if (!prompt || running) return;

        const pickedImages = mentionedImages
            .map((nodeId) => generationsRef.current.find((g) => g.nodeId === nodeId))
            .filter(Boolean)
            .map((g) => ({ nodeId: g!.nodeId, label: g!.label, url: g!.url, order: g!.order }));

        const turnId = `turn-${Date.now()}`;
        const epoch = viewEpochRef.current;
        const startedIn = opts?.threadId || activeThreadId || null;
        const sentReferences = referenceImages
            .filter((r) => r.url && !r.uploading)
            .map((r) => ({ id: r.id, url: r.url, name: r.name, sourceUrl: r.sourceUrl }));
        if (sentReferences.length > 0) {
            threadReferencesRef.current = [...threadReferencesRef.current, ...sentReferences];
            setReferenceImages((prev) => prev.filter((r) => r.uploading));
        }
        setTurns((prev) => [...prev, {
            id: turnId, message: prompt, activity: [], thoughts: [],
            status: 'running', thinkingEnabled: thinking, auto: opts?.auto,
            choice: opts?.choice,
            ...(sentReferences.length > 0 && { references: sentReferences }),
            // Carried on the message, not just the composer: reading the thread
            // back a day later, "fill in the prompt" means nothing unless it
            // still says which card it was pointed at.
            ...(targets.length > 0 && { targets }),
        }]);
        // Registered before the request, under the turn's id, because until the
        // server answers there is no run id — and without an entry the panel
        // reads as idle and re-enables the composer mid-turn.
        setActiveRuns((prev) => [...prev, { runId: turnId, threadId: startedIn, turnId }]);
        setMessage('');
        setMentionQuery(null);

        try {
            const run = await api.createAgentRun({
                profile: 'content-planner',
                message: prompt,
                projectUuid,
                threadId: opts?.threadId || activeThreadId || undefined,
                autonomy: 'suggest',
                thinking,
                context: {
                    ...canvasContext(),
                    // The canvas called this turn back, not the vendor. Lets the
                    // agent end with "all done" when the work is already there,
                    // instead of having to invent a plan or a question.
                    ...(opts?.auto && { autoContinuation: true }),
                    ...(mentioned.length > 0 && {
                        mentionedProducts: mentioned.map((p) => ({
                            uuid: p.uuid, name: p.name, visualizationType: p.visualizationType,
                        })),
                    }),
                    // Pictures the vendor pointed at. Resolved now rather than
                    // when they were picked, so one deleted in between is simply
                    // not there instead of naming a node that no longer exists.
                    ...(pickedImages.length > 0 && { mentionedGenerations: pickedImages }),
                    // Nodes the vendor pointed at with the agent button. Kept
                    // separate from mentionedGenerations: those are pictures,
                    // and a recipe card is not one.
                    ...(targets.length > 0 && {
                        mentionedNodes: targets.map((x) => ({
                            nodeId: x.nodeId, kind: x.kind, label: x.label,
                        })),
                    }),
                    // Inspiration attached in this conversation — every turn,
                    // not just the one that attached it: the agent may only ask
                    // what to take from a picture a turn later.
                    // The ones that came with THIS message are marked: by the
                    // third turn there are three pictures, and "the reference
                    // image uploaded now" has to mean something.
                    ...(threadReferencesRef.current.length > 0 && {
                        referenceImages: markLatestReferences(
                            threadReferencesRef.current, sentReferences,
                        ),
                    }),
                },
            });
            // Work and the dashboard are elsewhere and not watching.
            signalRunStarted();

            runIdRef.current = run.runId;
            patchTurn(turnId, { runId: run.runId });

            if (cancelledTurnsRef.current.has(turnId)) {
                // Stopped while it was being created — honour that now that the
                // run has a name, and never open the stream.
                cancelledTurnsRef.current.delete(turnId);
                void api.cancelAgentRun(run.runId).catch(() => undefined);
                setActiveRuns((prev) => prev.filter((r) => r.runId !== turnId));
                return;
            }

            // The run is now filed under its real thread, whatever is on screen.
            // Left attributed to the blank conversation it started in, clicking
            // "New chat" landed the vendor back on a null thread that still
            // matched this run: composer disabled, no turn visible, the agent
            // looking like it had died.
            setActiveRuns((prev) => prev.map((r) => (
                r.runId === turnId
                    ? { runId: run.runId, threadId: run.threadId ?? r.threadId, turnId }
                    : r
            )));
            if (run.threadId) {
                // The tab goes up either way, so a run the vendor navigated away
                // from is one click from being watched again.
                setOpenTabs((prev) => (
                    prev.includes(run.threadId!) ? prev : [run.threadId!, ...prev].slice(0, MAX_TABS)
                ));
                // Only follow the new conversation if they are still standing in
                // the one that opened it.
                if (viewEpochRef.current === epoch && !activeThreadId) {
                    setActiveThreadId(run.threadId);
                    rememberHasHistory(projectUuid, true);
                }
            }
            setMentioned([]);
            setMentionedImages([]);
            // Targets deliberately survive the send — see where the plan
            // lands. An exchange that begins "change this image" and continues
            // through a question is still about that image.

            streamsRef.current.set(run.runId, api.streamAgentRun(run.runId, (event) => {
                if (event.type === 'status') {
                    finishRun(run.runId);
                    void handleTerminal(turnId, event.data || {});
                    return;
                }
                if (event.type === 'error') {
                    finishRun(run.runId);
                    patchTurn(turnId, { status: 'error', error: event.data?.message || '' });
                    return;
                }
                // The model narrates regardless; we only keep it when the vendor
                // asked for reasoning, so "Thinking off" genuinely shows none.
                if (event.type === 'thinking') {
                    if (thinking && event.data?.message) {
                        appendTo(turnId, 'thoughts', String(event.data.message));
                    }
                    return;
                }
                const line = describeEvent(event);
                if (line) appendTo(turnId, 'activity', line);
            }));
        } catch (err: any) {
            // Drop the placeholder, or the panel stays "working" forever.
            setActiveRuns((prev) => prev.filter((r) => r.runId !== turnId));
            patchTurn(turnId, { status: 'error', error: err?.message });
        }
    }, [activeThreadId, api, appendTo, canvasContext, describeEvent, finishRun, handleTerminal,
        mentioned, mentionedImages, targets, referenceImages, patchTurn, projectUuid, running, thinking]);

    const stop = useCallback(() => {
        // Only this conversation's work. Stop used to abort whatever stream the
        // panel happened to be holding, which after a switch was someone
        // else's run.
        const mine = activeRuns.filter((r) => (r.threadId ?? null) === (activeThreadId ?? null));
        for (const r of mine) {
            // A placeholder has no run id yet; mark the turn so the reply that
            // is already in flight cancels itself on arrival.
            if (r.runId === r.turnId) cancelledTurnsRef.current.add(r.turnId);
            else void api.cancelAgentRun(r.runId).catch(() => undefined);
            finishRun(r.runId);
        }
        setTurns((prev) => prev.map((turn) => (
            turn.status === 'running'
                ? { ...turn, status: 'error', error: t('CONTENT_STUDIO.PLANNER.STOPPED', 'Stopped') }
                : turn
        )));
    }, [activeRuns, activeThreadId, api, finishRun, t]);

    const rate = useCallback((turn: Turn, value: 'up' | 'down') => {
        if (!turn.runId) return;
        // Optimistic, and a repeat click clears it — the server applies the
        // same toggle, so the two agree without a round trip.
        patchTurn(turn.id, { feedback: turn.feedback === value ? undefined : value });
        void api.rateAgentRun(turn.runId, value).catch(() => undefined);
    }, [api, patchTurn]);

    /**
     * The pictures a turn brought into being.
     *
     * A generation belongs to the most recent applied turn that did not already
     * have it — later turns snapshot it, so it stops being "new" for them and
     * is not shown twice down the conversation.
     */
    const imagesForTurn = useCallback((turn: Turn): CanvasGenerationInfo[] => {
        if (!turn.producedAfter) return [];
        const laterSnapshots = turns
            .slice(turns.findIndex((x) => x.id === turn.id) + 1)
            .filter((x) => x.producedAfter)
            .map((x) => new Set(x.producedAfter));
        const before = new Set(turn.producedAfter);
        return generations.filter((g) => !before.has(g.nodeId)
            && !laterSnapshots.some((s) => !s.has(g.nodeId)));
    }, [generations, turns]);

    const applyTurn = useCallback((turn: Turn, generate: boolean) => {
        if (!turn.plan || turn.applied) return;
        // Snapshot first: whatever appears after this is this turn's doing.
        const before = generationsRef.current.map((g) => g.nodeId);
        onApply(turn.plan, turn.summary || '', generate);
        patchTurn(turn.id, { applied: true, producedAfter: before });
        // Tell the server it landed. Applying happens here on the canvas, so
        // without this the next turn cannot tell acceptance from silence.
        if (turn.runId) void api.markAgentRunApplied(turn.runId).catch(() => undefined);

        // A plan that only finishes once its images exist asks to be called
        // back. Armed only on generate: applied without running anything, there
        // is nothing to wait for and the follow-up would never come.
        if (generate && turn.plan.continuation) {
            // Everything the plan will produce an image from — including a node
            // still waiting on its own 3D capture, which is not in the fire-list
            // and used to leave this armed against nothing.
            const awaiting = nodesAwaited(turn.plan);
            if (awaiting.length > 0 && activeThreadId) {
                // Handed to the server, which watches the project's own assets
                // and starts the follow-up itself. Nothing here waits for it,
                // so closing the tab no longer abandons the second half of a
                // job — and there is one rule for when it is due, not two.
                void api.armAgentContinuation({
                    projectUuid,
                    threadId: activeThreadId,
                    instruction: turn.plan.continuation,
                    expectedAssets: awaiting.length,
                    canvasSnapshot: canvasContext(),
                }).then(refreshContinuation).catch(() => undefined);
            }
        }
    }, [activeThreadId, api, canvasContext, onApply, patchTurn, projectUuid, refreshContinuation]);

    /**
     * Pick up a run the server started for us.
     *
     * The follow-up is no longer sent from here, so it has to be discovered:
     * while something is armed, watch the conversation for a run this panel did
     * not create and attach to it exactly as if it had.
     */
    useEffect(() => {
        // Only while looking at the conversation it belongs to. A follow-up in
        // another thread still runs — it is the server's now — and is rebuilt
        // from the record when the vendor opens that conversation.
        if (!continuation || !activeThreadId) return undefined;
        if (continuation.threadId !== activeThreadId) return undefined;

        const poll = window.setInterval(async () => {
            try {
                const { runs } = await api.getAgentThread(activeThreadId);
                const known = new Set(turnsRef.current.map((t) => t.runId).filter(Boolean));
                const fresh = (runs as CsAgentRun[]).filter((r) => !known.has(r.runId));
                if (fresh.length === 0) return;

                for (const run of fresh) {
                    setTurns((prev) => (prev.some((t) => t.runId === run.runId) ? prev : [...prev, {
                        id: run.runId,
                        runId: run.runId,
                        message: run.message,
                        activity: [],
                        thoughts: [],
                        status: 'running',
                        // Shown as a note rather than in the vendor's voice:
                        // the canvas asked this on their behalf.
                        auto: true,
                    }]));
                    resumeRun(run.runId, activeThreadId, run.runId);
                }
                void refreshContinuation();
            } catch {
                // A failed poll is not worth surfacing; the next one is 4s away.
            }
        }, 4000);

        return () => window.clearInterval(poll);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeThreadId, continuation]);

    // A retry that has landed, or failed again, is no longer a retry.
    useEffect(() => {
        setRetryingGenerations((prev) => {
            if (prev.size === 0) return prev;
            const next = new Set(
                [...prev].filter((nodeId) => {
                    const gen = generations.find((g) => g.nodeId === nodeId);
                    // Gone from the canvas, or replaced by a fresh attempt.
                    return !!gen && gen.status === 'failed';
                }),
            );
            return next.size === prev.size ? prev : next;
        });
    }, [generations]);

    // ── Status label while working ───────────────────────────────
    const statusLabel = useCallback((turn: Turn) => {
        if (turn.activity.length > 0) return turn.activity[turn.activity.length - 1];
        // "Thinking…" over a turn with thinking switched off described something
        // that was not happening. With it off the honest word is working.
        if (turn.thinkingEnabled) return t('CONTENT_STUDIO.PLANNER.THINKING', 'Thinking…');
        return t('CONTENT_STUDIO.PLANNER.WORKING', 'Working…');
    }, [t]);

    /**
     * The plan waiting on an answer, if there is one.
     *
     * Only ever the latest: an older proposal the vendor scrolled past is not
     * what they are being asked about, and stacking two sets of buttons above
     * the composer would make it unclear which was which.
     */
    const pendingTurn = useMemo(
        () => [...turns].reverse().find(
            (turn) => turn.plan && !turn.applied && !turn.denied && turn.status !== 'error',
        ),
        [turns],
    );

    /**
     * The message split into plain text and the products named in it.
     *
     * Drives the pill drawn behind the field. Matched against what they have
     * actually mentioned rather than any "@word", so a stray @ is left as
     * ordinary text instead of being dressed up as a product that is not there.
     */
    /** A picked file becomes a hosted reference the pipeline can reach. */
    const addReferenceFile = useCallback(async (file: File) => {
        const id = nextReferenceId();
        setRefError(null);
        setReferenceImages((prev) => [...prev, { id, url: '', name: file.name, uploading: true }]);
        try {
            const { url } = await api.uploadStyleRef(file);
            setReferenceImages((prev) => prev.map((r) => (r.id === id ? { ...r, url, uploading: false } : r)));
        } catch (err: any) {
            setReferenceImages((prev) => prev.filter((r) => r.id !== id));
            setRefError(err?.response?.data?.message || err?.message
                || t('CONTENT_STUDIO.PLANNER.REF_UPLOAD_FAILED', 'That picture could not be uploaded.'));
        }
    }, [api, t]);

    /**
     * A pasted address becomes a hosted copy through the guarded server fetch —
     * remote addresses expire and hotlink-block, and the server checks where
     * the address actually points before touching it.
     */
    const addReferenceUrl = useCallback(async (raw: string) => {
        const value = raw.trim();
        if (!value || refUrlBusy) return;
        setRefUrlBusy(true);
        setRefError(null);
        try {
            const { url, sourceUrl } = await api.inspirationFromUrl(value);
            const id = nextReferenceId();
            let name = 'reference';
            try { name = new URL(sourceUrl || value).hostname.replace(/^www\./, ''); } catch { /* keep default */ }
            setReferenceImages((prev) => [...prev, { id, url, sourceUrl: sourceUrl || value, name }]);
            setRefUrlValue('');
            setRefUrlOpen(false);
        } catch (err: any) {
            setRefError(err?.response?.data?.message || err?.message
                || t('CONTENT_STUDIO.PLANNER.REF_URL_FAILED', 'That address could not be fetched.'));
        } finally {
            setRefUrlBusy(false);
        }
    }, [api, refUrlBusy, t]);

    // "Use AI" on a card lands here: the question is typed into the composer,
    // the panel takes focus, and the vendor presses send — or edits it first.
    useEffect(() => {
        if (!handoff || handoffId.current === handoff.id) return;
        handoffId.current = handoff.id;
        if (handoff.target) {
            const target = handoff.target;
            setTargets((prev) => (
                prev.some((x) => x.nodeId === target.nodeId) ? prev : [...prev, target]
            ));
        }
        // A pointed-at node carries no words, so an empty text must not wipe
        // whatever the vendor was already typing.
        if (handoff.text) setMessage(handoff.text);

        if (handoff.referenceUrl) {
            const url = handoff.referenceUrl;
            let name = 'reference';
            try {
                name = new URL(url).pathname.split('/').pop() || name;
            } catch {
                /* keep the default */
            }

            // Appended rather than replacing: the vendor may have added their
            // own reference before the handover landed.
            setReferenceImages((prev) =>
                prev.some((r) => r.url === url)
                    ? prev
                    : [...prev, { id: `handoff-${handoff.id}`, url, sourceUrl: url, name }],
            );
        }

        window.setTimeout(() => {
            inputRef.current?.focus();
            const el = inputRef.current;
            if (el) el.setSelectionRange(el.value.length, el.value.length);
        }, 0);
        onHandoffConsumed?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handoff]);

    const denyTurn = useCallback((turn: Turn) => {
        // Recorded rather than removed: the conversation should still show what
        // was offered and that they said no to it.
        patchTurn(turn.id, { denied: true });
    }, [patchTurn]);

    /**
     * Token spend expressed in credits, so the number sits in the same unit as
     * everything else the vendor sees. Display-only for now — agent turns are
     * not debited from the ledger; generations still are.
     */
    const agentCreditsUsed = Math.max(1, Math.round(threadTokens / TOKENS_PER_CREDIT));


    const threadTitle = (thread: CsAgentThread) =>
        thread.title || t('CONTENT_STUDIO.PLANNER.UNTITLED_CHAT', 'New chat');

    // ── Collapsed ────────────────────────────────────────────────
    if (!open) {
        if (collapsedHidden) return null;
        const latest = turns[turns.length - 1];
        return (
            <div
                className={cn(
                    // Sitting up off the floor, and carrying real lift.
                    //
                    // The opposite call from the panel, deliberately: that is a
                    // large surface the vendor reads on, so it stays flat. This
                    // is a small control floating over the canvas, and it has to
                    // look like it is above the work rather than printed on it —
                    // flush to the bottom edge with a hairline shadow, it read as
                    // part of the page furniture and was easy to miss on an empty
                    // project, which is exactly where it is now the first thing
                    // to use.
                    'absolute bottom-10 left-1/2 z-20 -translate-x-1/2',
                    'w-[min(680px,calc(100%-48px))]',
                    'flex items-center gap-2 rounded-xl border bg-card/95 py-2 pe-2 ps-5',
                    'backdrop-blur-xl transition-shadow',
                    'shadow-lg hover:border-ring hover:shadow-xl',
                )}
            >
                {running ? (
                    <>
                        <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
                        <button
                            type="button"
                            onClick={() => onOpenChange(true)}
                            className="min-w-0 flex-1 truncate py-1.5 text-start text-sm text-muted-foreground"
                        >
                            {latest ? statusLabel(latest) : t('CONTENT_STUDIO.PLANNER.THINKING', 'Thinking…')}
                        </button>
                    </>
                ) : (
                    <Input
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (message.trim()) { onOpenChange(true); void send(message); }
                            }
                        }}
                        placeholder={t('CONTENT_STUDIO.PLANNER.COMPOSER_PLACEHOLDER',
                            'Describe what you want to create…') as string}
                        className="h-9 border-0 bg-transparent px-0 text-sm focus-visible:ring-0 md:text-sm dark:bg-transparent"
                    />
                )}
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            className="shrink-0"
                            onClick={() => onOpenChange(true)}
                            aria-label={t('CONTENT_STUDIO.PLANNER.OPEN_PANEL', 'Open the conversation') as string}
                        >
                            {turns.length > 0 ? <Ellipsis className="size-4" /> : <Plus className="size-4" />}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {t('CONTENT_STUDIO.PLANNER.OPEN_PANEL', 'Open the conversation')}
                    </TooltipContent>
                </Tooltip>
                <SendButton
                    enabled={!!message.trim() && !running}
                    onClick={() => { if (message.trim()) { onOpenChange(true); void send(message); } }}
                    ariaLabel={t('CONTENT_STUDIO.PLANNER.SEND', 'Send') as string}
                />
            </div>
        );
    }

    // ── Expanded ─────────────────────────────────────────────────
    const historyThreads = threads.filter((thread) =>
        !historyQuery.trim()
        || threadTitle(thread).toLowerCase().includes(historyQuery.trim().toLowerCase()));

    return (
        <div
            // Floating rather than welded to the edge: the canvas reads as the
            // surface being worked on, and the panel as something resting on
            // top of it. Held in physical `right` rather than a logical inset
            // because the resize drag maths is anchored to that same edge.
            style={{
                top: AGENT_PANEL_MARGIN,
                right: AGENT_PANEL_MARGIN,
                bottom: AGENT_PANEL_MARGIN,
                width: panelWidth,
            }}
            className={cn(
                'absolute z-30 flex flex-col rounded-xl border bg-card/95 backdrop-blur-md',
                // Barely there. The border and the gap already separate it from
                // the canvas; a heavy drop shadow on a panel this size reads as
                // weight rather than depth, and it is the surface the vendor is
                // reading.
                'shadow-sm',
                // Deliberately not clipped. Menus that open from the composer
                // are wider than the room left beside their button, so clipping
                // the panel cut their edges off; they belong over the canvas,
                // like any other menu.
            )}
        >
            {/* Drag to widen. On the left edge because that is the only side
                with canvas behind it — the others are against the window. */}
            <div
                role="separator"
                aria-orientation="vertical"
                aria-label={t('CONTENT_STUDIO.PLANNER.RESIZE', 'Resize the panel') as string}
                onMouseDown={startResize}
                className={cn(
                    'absolute top-0 bottom-0 left-0 z-[5] w-2 cursor-col-resize',
                    'after:absolute after:top-2 after:bottom-2 after:left-[3px] after:w-0.5',
                    'after:bg-border after:opacity-0 hover:after:opacity-100 active:after:opacity-100',
                    'after:content-[""]',
                )}
            />

            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3">
                <div className="flex flex-1 items-center gap-2 font-heading text-sm font-medium tracking-tight text-foreground">
                    {t('CONTENT_STUDIO.PLANNER.PANEL_TITLE', 'Studio Agent')}
                    {/* The agent is reachable. Quiet, and the only thing on
                        screen that would say otherwise if it were not. */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span
                                aria-label={t('CONTENT_STUDIO.PLANNER.CONNECTED', 'Connected') as string}
                                className="size-[7px] shrink-0 rounded-full bg-success ring-[3px] ring-success/15"
                            />
                        </TooltipTrigger>
                        <TooltipContent>
                            {t('CONTENT_STUDIO.PLANNER.CONNECTED', 'Connected')}
                        </TooltipContent>
                    </Tooltip>
                </div>
                <Button variant="ghost" size="icon-sm" aria-label={t('COMMON.CLOSE') as string} onClick={() => onOpenChange(false)}>
                    <X className="size-4" />
                </Button>
            </div>

            {/* Tab strip */}
            <div className="relative flex items-center gap-1 border-b px-2.5 pb-2">
                <div className="flex min-w-0 flex-1 gap-1 overflow-hidden">
                    {openTabs.map((id) => {
                        const thread = threads.find((th) => th.threadId === id);
                        const active = id === activeThreadId;
                        // Something still working in a conversation the vendor
                        // has left. Without a mark, walking away from a run and
                        // coming back to a quiet tab reads as it having died.
                        const busy = !active && activeRuns.some((r) => r.threadId === id);
                        return (
                            <div
                                key={id}
                                data-testid="planner-thread-tab"
                                onClick={() => { if (!active) void loadThread(id); }}
                                className={cn(
                                    'flex min-w-0 max-w-[130px] cursor-pointer items-center gap-1 px-2 py-1',
                                    'transition-colors hover:bg-muted',
                                    active && 'bg-muted',
                                )}
                            >
                                {(active || busy) && (
                                    <span
                                        className={cn(
                                            'size-[5px] shrink-0 rounded-full bg-muted-foreground',
                                            busy && 'animate-pulse motion-reduce:animate-none',
                                        )}
                                    />
                                )}
                                <span
                                    className={cn(
                                        'min-w-0 truncate text-xs',
                                        active ? 'text-foreground' : 'text-muted-foreground',
                                    )}
                                >
                                    {thread ? threadTitle(thread) : t('CONTENT_STUDIO.PLANNER.UNTITLED_CHAT', 'New chat')}
                                </span>
                                <span
                                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); closeTab(id); }}
                                    className="shrink-0 cursor-pointer px-0.5 leading-none text-muted-foreground hover:text-foreground"
                                >
                                    <X className="size-3" />
                                </span>
                            </div>
                        );
                    })}
                </div>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            data-testid="planner-new-conversation"
                            onClick={newConversation}
                            aria-label={t('CONTENT_STUDIO.PLANNER.NEW_CONVERSATION', 'New conversation') as string}
                        >
                            <Plus className="size-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {t('CONTENT_STUDIO.PLANNER.NEW_CONVERSATION', 'New conversation')}
                    </TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={t('CONTENT_STUDIO.PLANNER.HISTORY', 'Chat history') as string} onClick={() => setHistoryOpen((v) => !v)}>
                            <History className="size-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {t('CONTENT_STUDIO.PLANNER.HISTORY', 'Chat history')}
                    </TooltipContent>
                </Tooltip>

                {/* History dropdown. Its own absolutely-positioned surface
                    rather than a popover: the search field inside must take
                    focus on open without a focus trap fighting it. */}
                {historyOpen && (
                    <div className="absolute end-2 top-full z-40 mt-1 w-[300px] overflow-hidden rounded-xl border bg-card shadow-sm">
                        <div className={cn(PANEL_LABEL, 'px-3 pt-2.5 pb-1 font-medium')}>
                            {t('CONTENT_STUDIO.PLANNER.CHATS', 'Chats')}
                        </div>
                        <div className="px-2.5 pb-2">
                            <Input
                                autoFocus
                                value={historyQuery}
                                onChange={(e) => setHistoryQuery(e.target.value)}
                                placeholder={t('CONTENT_STUDIO.PLANNER.SEARCH_CHATS', 'Search chats…') as string}
                            />
                        </div>
                        <div className="max-h-[260px] overflow-y-auto pb-1">
                            {historyThreads.length === 0 && (
                                <div className={cn(PANEL_LABEL, 'px-3 py-2')}>
                                    {t('CONTENT_STUDIO.PLANNER.NO_CHATS', 'No conversations yet')}
                                </div>
                            )}
                            {historyThreads.map((thread) => (
                                <button
                                    key={thread.threadId}
                                    type="button"
                                    onClick={() => openThread(thread.threadId)}
                                    className="flex w-full items-center gap-2 px-3 py-2 text-start hover:bg-muted"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-xs text-foreground">
                                            {threadTitle(thread)}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground">
                                            {relativeTime(thread.lastRunAt || thread.createdAt, t)}
                                        </div>
                                    </div>
                                    {thread.threadId === activeThreadId && (
                                        <Check className="size-3.5 shrink-0 text-foreground" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Conversation */}
            <div
                ref={scrollRef}
                className={cn(
                    'flex-1 overflow-y-auto px-4 py-4',
                    // Centred while there is nothing to read: a line of guidance
                    // pinned under the tabs left the panel looking top-heavy and
                    // mostly empty.
                    turns.length === 0 && 'flex items-center justify-center',
                )}
            >
                {turns.length === 0 && (
                    <p className={cn(PANEL_LABEL, 'px-6 text-center leading-relaxed')}>
                        {t('CONTENT_STUDIO.PLANNER.EMPTY_STATE',
                            'Describe what you want to create. The agent knows your catalog and what is on the canvas.')}
                    </p>
                )}

                {turns.map((turn) => (
                    <div key={turn.id} className="mb-5">
                        {/* The vendor's message — or, for a turn the canvas
                            started once a plan's images finished, a note saying
                            so. Putting that in their bubble would be quoting
                            them saying something they never said. */}
                        {turn.auto && (
                            <p className="mb-2.5 text-sm break-words text-muted-foreground italic">
                                {t('CONTENT_STUDIO.PLANNER.CONTINUED',
                                    'The images finished, so I carried on: {{instruction}}',
                                    { instruction: turn.message })}
                            </p>
                        )}
                        {!turn.auto && !turn.choice && (
                            <div className="mb-2.5 flex flex-col">
                                <Bubble variant="muted" align="end" className="max-w-[82%]">
                                    <BubbleContent className="px-3.5 py-2 text-sm break-words">
                                        {/* The pictures this message carried, above
                                            its text — the attachment travels with
                                            the message, not the composer. */}
                                        {(turn.references?.length ?? 0) > 0 && (
                                            <div className="mb-1.5 flex flex-wrap gap-1">
                                                {turn.references!.map((ref) => (
                                                    <img
                                                        key={ref.id}
                                                        src={ref.url}
                                                        alt={ref.name || ''}
                                                        className="size-[52px] rounded-lg border object-cover"
                                                    />
                                                ))}
                                            </div>
                                        )}
                                        {/* What this message was pointed at, above its
                                            text — reading the thread back, "fill in the
                                            prompt" means nothing without the card it
                                            named. */}
                                        {(turn.targets?.length ?? 0) > 0 && (
                                            <div className="mb-1.5 flex flex-wrap gap-1">
                                                {turn.targets!.map((tg) => (
                                                    <span
                                                        key={tg.nodeId}
                                                        className={cn(
                                                            'inline-flex max-w-[200px] items-center gap-1 bg-background/60 py-0.5 pe-1.5 text-[11px]',
                                                            tg.thumbnailUrl ? 'ps-1' : 'ps-2',
                                                        )}
                                                    >
                                                        {tg.thumbnailUrl && (
                                                            <img
                                                                src={tg.thumbnailUrl}
                                                                alt=""
                                                                className="size-[18px] shrink-0 rounded-full object-cover"
                                                            />
                                                        )}
                                                        <span className="min-w-0 truncate">{tg.label}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {turn.message}
                                    </BubbleContent>
                                </Bubble>
                            </div>
                        )}
                        {/* Picked off a card rather than typed. Shown as what it
                            was — a choice — instead of quoted back as a message
                            they never composed. */}
                        {turn.choice && <ChoiceChip choice={turn.choice} />}

                        {turn.thinkingEnabled && <ThoughtDisclosure thoughts={turn.thoughts} />}

                        {/* What the turn did, and is doing. The trail stays put
                            once finished, so a turn can be read back rather than
                            leaving only its last flicker of status. */}
                        <ActionTrail
                            actions={turn.plan
                                ? planActions(turn.plan, t as any)
                                : turn.activity.map((line) => ({ verb: line }))}
                            live={turn.status === 'running'}
                        />

                        {turn.status === 'running' && turn.activity.length === 0 && !turn.plan
                            && <AgentStatus label={statusLabel(turn)} />}

                        {turn.status === 'error' && (
                            <p className="text-sm text-muted-foreground">{turn.error}</p>
                        )}

                        {turn.question && (
                            <div>
                                {turn.question.context && (
                                    <p className={cn(PANEL_BODY, 'mb-1')}>{turn.question.context}</p>
                                )}
                                <p className={cn(PANEL_BODY, turn.question.options?.length && 'mb-2.5')}>
                                    {turn.question.question}
                                </p>
                                {/* Product choices render as pictures — a vendor
                                    recognises their own product by sight long
                                    before they read its name. Options without a
                                    product stay ordinary buttons: rendering
                                    "Show me all three" as a card gave it an
                                    empty thumbnail and it read as a fourth,
                                    broken product. */}
                                {(turn.question.options || []).some((o) => o.thumbnailUrl) && (
                                    <div className="mb-1.5 flex flex-wrap gap-2">
                                        {(turn.question.options || [])
                                            .filter((o) => o.thumbnailUrl)
                                            .map((option) => (
                                            <button
                                                key={option.label}
                                                type="button"
                                                data-testid="agent-option"
                                                disabled={running}
                                                onClick={() => void send(option.label, {
                                                    choice: {
                                                        label: option.productName || option.label,
                                                        thumbnailUrl: option.thumbnailUrl,
                                                    },
                                                })}
                                                className={cn(
                                                    'w-24 rounded-lg border p-1.5 text-start transition-colors',
                                                    'enabled:hover:border-ring enabled:hover:bg-muted',
                                                    'disabled:cursor-default',
                                                )}
                                            >
                                                <img
                                                    src={option.thumbnailUrl}
                                                    alt=""
                                                    className="mb-1.5 block h-[72px] w-full rounded-md bg-muted object-cover"
                                                />
                                                <span className="block truncate text-[11px] text-foreground">
                                                    {option.productName || option.label}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {(turn.question.options || [])
                                    .filter((o) => !o.thumbnailUrl)
                                    .map((option) => (
                                        <button
                                            key={option.label}
                                            type="button"
                                            data-testid="agent-option"
                                            disabled={running}
                                            onClick={() => void send(option.label, {
                                                choice: { label: option.label },
                                            })}
                                            className={cn(
                                                'mb-1.5 block w-full rounded-lg border px-3 py-2 text-start transition-colors',
                                                'enabled:hover:border-ring enabled:hover:bg-muted',
                                                'disabled:cursor-default',
                                            )}
                                        >
                                            <span className={cn(PANEL_BODY, 'block font-medium')}>
                                                {option.label}
                                            </span>
                                            {option.hint && (
                                                <span className={cn(PANEL_LABEL, 'block')}>{option.hint}</span>
                                            )}
                                        </button>
                                    ))}

                                {(turn.question.options || []).length > 0 && (
                                    customAnswerFor === turn.id ? (
                                        <Textarea
                                            autoFocus
                                            data-testid="agent-option-custom-input"
                                            value={customAnswer}
                                            placeholder={t(
                                                'CONTENT_STUDIO.PLANNER.ANSWER_OTHER_PLACEHOLDER',
                                                'Type your answer…',
                                            ) as string}
                                            onChange={(e) => setCustomAnswer(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Escape') {
                                                    setCustomAnswerFor(null);
                                                    setCustomAnswer('');
                                                    return;
                                                }
                                                // Enter sends; Shift+Enter is a
                                                // newline, matching the composer.
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    const answer = customAnswer.trim();
                                                    if (!answer || running) return;
                                                    setCustomAnswerFor(null);
                                                    setCustomAnswer('');
                                                    void send(answer);
                                                }
                                            }}
                                            className="mb-1.5 min-h-[62px] resize-y"
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            data-testid="agent-option-custom"
                                            disabled={running}
                                            onClick={() => { setCustomAnswer(''); setCustomAnswerFor(turn.id); }}
                                            className={cn(
                                                'mb-1.5 block w-full rounded-lg border border-dashed px-3 py-2 text-start transition-colors',
                                                'enabled:hover:border-ring enabled:hover:bg-muted',
                                                'disabled:cursor-default',
                                            )}
                                        >
                                            <span className="text-sm font-medium text-muted-foreground">
                                                {t('CONTENT_STUDIO.PLANNER.ANSWER_OTHER', 'Something else…')}
                                            </span>
                                        </button>
                                    )
                                )}
                            </div>
                        )}

                        {/* Only once it has been decided. While a plan is
                            waiting it lives in the bar above the composer,
                            where scrolling back through the history cannot
                            carry the buttons off the screen. */}
                        {turn.plan && (turn.applied || turn.denied) && (
                            <PlanCard
                                turn={turn} creditBalance={creditBalance}
                                running={running} onApply={applyTurn}
                            />
                        )}

                        {!turn.plan && !turn.question && turn.status === 'done' && turn.summary && (
                            <p className={PANEL_BODY}>{turn.summary}</p>
                        )}

                        {/* What this turn actually produced. The vendor asked
                            here, so the answer belongs here too — otherwise the
                            only way to see it is to go hunting on the canvas. */}
                        <TurnResults
                            images={imagesForTurn(turn)}
                            onOpen={(nodeId) => onOpenGeneration?.(nodeId)}
                            retrying={retryingGenerations}
                            onRetry={onRetryGeneration ? (nodeId) => {
                                // Marked before the job is created so the tile
                                // changes the moment they click, rather than
                                // when the canvas gets around to it.
                                setRetryingGenerations((prev) => new Set(prev).add(nodeId));
                                onRetryGeneration(nodeId);
                            } : undefined}
                        />

                        {turn.status === 'done' && turn.runId && (
                            <FeedbackControls value={turn.feedback} onRate={(v) => rate(turn, v)} />
                        )}
                    </div>
                ))}

                {/* Work the canvas is waiting on, in the conversation rather
                    than tucked under the composer — it is part of what the
                    agent is doing, and reads as a turn still in progress. */}
                {continuation && (
                    <div className="mb-5">
                        <AgentStatus label={t('CONTENT_STUDIO.PLANNER.CONTINUATION_WAITING',
                            'When the images are done: {{instruction}}',
                            { instruction: continuation.instruction }) as string} />
                        <button
                            type="button"
                            onClick={() => {
                                // Called off on the server: the sweep is what
                                // would otherwise start it, and it cannot see a
                                // decision made only in this tab.
                                const id = continuation.continuationId;
                                setContinuation(null);
                                void api.cancelAgentContinuation(id).catch(() => undefined);
                            }}
                            className="text-[11px] text-muted-foreground underline hover:text-foreground"
                        >
                            {t('CONTENT_STUDIO.PLANNER.CONTINUATION_CANCEL', 'Don\'t continue')}
                        </button>
                    </div>
                )}
            </div>

            {/* Mention picker. Deliberately an ordinary block in the panel's
                flow rather than a popover: a Radix surface takes focus, and the
                composer's caret is what the insertion point is read from. */}
            {mentionQuery != null && (
                <div ref={mentionRef} className="mx-4 mb-2 overflow-hidden rounded-xl border bg-card shadow-sm">
                    {/* Its own search field: opening the list from "+" gives no
                        typed query to filter by, and the composer's caret is a
                        poor place to search a catalog from. */}
                    <div className="px-2.5 pt-2.5 pb-1.5">
                        <Input
                            autoFocus
                            value={mentionQuery}
                            onChange={(e) => { setMentionQuery(e.target.value); setMentionIndex(0); }}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); inputRef.current?.focus(); return; }
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setMentionIndex((i) => (i + 1) % Math.max(1, mentionMatches.length));
                                    return;
                                }
                                if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setMentionIndex((i) => (i - 1 + mentionMatches.length) % Math.max(1, mentionMatches.length));
                                    return;
                                }
                                if ((e.key === 'Enter' || e.key === 'Tab') && mentionMatches[mentionIndex]) {
                                    e.preventDefault();
                                    acceptMention(mentionMatches[mentionIndex]);
                                }
                            }}
                            placeholder={t('CONTENT_STUDIO.PLANNER.SEARCH_PRODUCTS', 'Search products…') as string}
                        />
                    </div>
                    <div className="max-h-[280px] overflow-y-auto pb-1">
                    {mentionMatches.length === 0 && (
                        <div className={cn(PANEL_LABEL, 'px-3.5 py-2')}>
                            {t('CONTENT_STUDIO.PLANNER.NO_PRODUCTS', 'No matching products')}
                        </div>
                    )}
                    {mentionMatches.map((product, i) => (
                        <button
                            key={product.uuid}
                            type="button"
                            data-testid="mention-option"
                            onMouseEnter={() => setMentionIndex(i)}
                            // Keep the caret in the editor: a plain click blurs
                            // it before the handler runs.
                            onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                            onClick={() => acceptMention(product)}
                            className={cn(
                                'flex w-full items-center gap-2.5 px-3 py-2 text-start',
                                i === mentionIndex && 'bg-muted',
                            )}
                        >
                            {product.thumbnailPath && (
                                <img
                                    src={product.thumbnailPath}
                                    alt=""
                                    className="size-6 shrink-0 rounded-md border object-cover"
                                />
                            )}
                            <span className={cn(PANEL_BODY, 'min-w-0 flex-1 truncate')}>
                                {product.name}
                            </span>
                        </button>
                    ))}
                    </div>
                </div>
            )}

            {/* Composer */}
            <div
                className={cn(
                    // Sticky rather than scrolling: the plan waiting on an
                    // answer has to stay reachable while the vendor reads back
                    // through what led to it.
                    'sticky bottom-0 z-[2] bg-card px-4 pb-4',
                )}
            >
                {pendingTurn && (
                    <PendingPlanBar
                        turn={pendingTurn}
                        creditBalance={creditBalance}
                        running={running}
                        onApply={applyTurn}
                        onDeny={denyTurn}
                    />
                )}
                {threadTokens > 0 && (
                    <div className="mb-1.5 text-[11px] text-muted-foreground">
                        {t('CONTENT_STUDIO.PLANNER.CREDITS_USED', {
                            defaultValue: '{{count}} credits used by the agent',
                            count: agentCreditsUsed,
                        })}
                    </div>
                )}

                {/* What this message is pointing at, above the box it
                    belongs to: chips inside the field pushed the caret
                    around and read as content the vendor had typed. */}
                {refUrlOpen && (
                    <div className="mb-1.5 flex items-center gap-1.5">
                        <div className="relative flex-1">
                            <Input
                                autoFocus
                                value={refUrlValue}
                                disabled={refUrlBusy}
                                onChange={(e) => setRefUrlValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); void addReferenceUrl(refUrlValue); }
                                    if (e.key === 'Escape') { setRefUrlOpen(false); setRefUrlValue(''); }
                                }}
                                placeholder={t('CONTENT_STUDIO.PLANNER.REFERENCE_URL_PLACEHOLDER',
                                    'Paste the address of a picture…') as string}
                                className={cn(refUrlBusy && 'pe-8')}
                            />
                            {refUrlBusy && (
                                <Loader2 className="absolute end-2.5 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
                            )}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={refUrlBusy || !refUrlValue.trim()}
                            onClick={() => void addReferenceUrl(refUrlValue)}
                        >
                            {t('CONTENT_STUDIO.PLANNER.REFERENCE_URL_ADD', 'Add')}
                        </Button>
                    </div>
                )}
                {refError && (
                    <div className="mb-1.5 text-[11px] text-destructive">{refError}</div>
                )}
                <div className="mb-1.5 flex flex-wrap gap-1.5">

                    {/* Inspiration they attached: thumbnail, name, and a way
                        out. An uploading one shows its spinner in place so the
                        row does not jump when the url arrives. */}
                    {referenceImages.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {referenceImages.map((ref) => (
                                <ComposerChip
                                    key={ref.id}
                                    dashed
                                    busy={ref.uploading}
                                    thumbnailUrl={ref.url}
                                    label={ref.name || t('CONTENT_STUDIO.PLANNER.REFERENCE_CHIP', 'Reference') as string}
                                    onRemove={() => setReferenceImages((prev) => prev.filter((r) => r.id !== ref.id))}
                                />
                            ))}
                        </div>
                    )}

                    {/**
                      * What this message is ABOUT, pointed at from the canvas.
                      *
                      * A recipe card is not a picture, so it cannot go in the
                      * thumbnail strip — and "the Lifestyle Scene one" typed
                      * out is exactly the ambiguity the button exists to
                      * remove. The chip names the node so both sides can see
                      * which one is meant before anything is sent.
                      */}
                    {targets.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {targets.map((target) => (
                                <ComposerChip
                                    key={target.nodeId}
                                    thumbnailUrl={target.thumbnailUrl}
                                    label={target.label}
                                    onRemove={() => setTargets(
                                        (prev) => prev.filter((x) => x.nodeId !== target.nodeId),
                                    )}
                                />
                            ))}
                        </div>
                    )}

                    {/* Pictures the vendor pointed at, as thumbnails — "this
                        one" said by showing rather than describing. */}
                    {mentionedImages.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {mentionedImages.map((nodeId) => {
                                const image = generations.find((g) => g.nodeId === nodeId);
                                if (!image) return null;
                                return (
                                    <div key={nodeId} className="relative size-11 overflow-hidden rounded-lg border">
                                        <img
                                            src={image.thumbnailUrl || image.url}
                                            alt={image.label}
                                            className="size-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setMentionedImages(
                                                (prev) => prev.filter((id) => id !== nodeId),
                                            )}
                                            className="absolute top-0 end-0 flex size-4 items-center justify-center bg-foreground/60 text-background"
                                        >
                                            <X className="size-2.5" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {mentioned.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {mentioned.map((product) => (
                                <ComposerChip
                                    key={product.uuid}
                                    thumbnailUrl={product.thumbnailPath}
                                    label={product.name}
                                    onRemove={() => removeMention(product)}
                                />
                            ))}
                        </div>
                    )}

                </div>

                <div className="rounded-xl border bg-card transition-colors focus-within:border-ring">
                    {/**
                      * The box, with mentions as real elements.
                      *
                      * This used to be a textarea with the badges painted on a
                      * layer behind it. That only works while the painted layer
                      * advances by exactly what the hidden text advances by, so
                      * the badge could never carry a picture, padding or a
                      * margin — every attempt bought one of those and the caret
                      * drifted away from the words. A contenteditable badge is
                      * simply an element: the caret steps over it, backspace
                      * takes it whole, and it can look like the chip above it.
                      */}
                    <MentionComposer
                        ref={composerRef}
                        value={message}
                        disabled={running}
                        placeholder={t('CONTENT_STUDIO.PLANNER.COMPOSER_PLACEHOLDER',
                            'Describe what you want to create…') as string}
                        products={mentionableProducts.map((p) => ({
                            uuid: p.uuid, name: p.name, thumbnailPath: p.thumbnailPath,
                        }))}
                        onChange={(value, caretText) => {
                            setMessage(value);
                            // The query is bounded by the caret, so it is read from
                            // the text BEFORE it rather than from the whole box.
                            setMentionQuery(queryFromText(caretText, caretText.length));
                            setMentionIndex(0);
                        }}
                        onSubmit={() => { void send(message); }}
                        onKeyDown={(e) => {
                            if (mentionQuery != null && mentionMatches.length > 0) {
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setMentionIndex((i) => (i + 1) % mentionMatches.length);
                                    return true;
                                }
                                if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
                                    return true;
                                }
                                if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    acceptMention(mentionMatches[mentionIndex]);
                                    return true;
                                }
                                if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); return true; }
                            }
                            return false;
                        }}
                    />

                    {/* Toolbar row. Wraps rather than pushing Send past the
                        panel edge, where it was clipped away entirely. */}
                    <div className="flex flex-wrap items-center gap-1 gap-y-1 px-2 pt-0.5 pb-2">
                        <DropdownMenu open={plusMenuOpen} onOpenChange={setPlusMenuOpen}>
                            {/* Labelled rather than wrapped in a tooltip: a
                                tooltip trigger around a menu trigger loses the
                                anchor and the menu opens off-screen. */}
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    disabled={running}
                                    aria-label={t('CONTENT_STUDIO.PLANNER.ADD_TO_MESSAGE', 'Add to your message') as string}
                                >
                                    <Plus className="size-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="top" align="start">
                                <DropdownMenuItem
                                    disabled={mentionableProducts.length === 0}
                                    onClick={() => {
                                        setPlusMenuOpen(false);
                                        setMentionQuery('');
                                        inputRef.current?.focus();
                                    }}
                                >
                                    {t('CONTENT_STUDIO.PLANNER.MENU_ADD_PRODUCT', 'Add a product')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => {
                                        setPlusMenuOpen(false);
                                        refFileInputRef.current?.click();
                                    }}
                                >
                                    {t('CONTENT_STUDIO.PLANNER.MENU_ADD_REFERENCE', 'Add a reference image')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => {
                                        setPlusMenuOpen(false);
                                        setRefUrlOpen(true);
                                    }}
                                >
                                    {t('CONTENT_STUDIO.PLANNER.MENU_REFERENCE_URL', 'Reference from a link')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {/* Never rendered visibly: the menu item is the button. */}
                        <input
                            type="file"
                            accept="image/*"
                            ref={refFileInputRef}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file) void addReferenceFile(file);
                            }}
                            className="hidden"
                        />

                        {/* Reasoning toggle. Off by default; the label states
                            the cost so it reads as a trade rather than a
                            quality switch to leave on forever. */}
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="outline"
                                    onClick={() => setThinking((v) => !v)}
                                    aria-pressed={thinking}
                                    className={cn(TOOLBAR_CHIP, thinking
                                        ? 'border-ring bg-muted text-foreground'
                                        : 'text-muted-foreground')}
                                >
                                    <Brain className="size-3.5" />
                                    {t('CONTENT_STUDIO.PLANNER.THINKING_TOGGLE', 'Thinking')}
                                    <span className="opacity-75">
                                        {thinking
                                            ? t('CONTENT_STUDIO.PLANNER.ON', 'on')
                                            : t('CONTENT_STUDIO.PLANNER.OFF', 'off')}
                                    </span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {t('CONTENT_STUDIO.PLANNER.THINKING_HINT',
                                    'Let the agent reason first — better on open-ended briefs, slower and more expensive')}
                            </TooltipContent>
                        </Tooltip>

                        {/* Point at a picture on the canvas. */}
                        {onPickGeneration && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        onClick={async () => {
                                            const nodeId = await onPickGeneration();
                                            if (!nodeId) return;
                                            setMentionedImages((prev) => (
                                                prev.includes(nodeId) ? prev : [...prev, nodeId]
                                            ));
                                            inputRef.current?.focus();
                                        }}
                                        className={cn(TOOLBAR_CHIP, 'text-muted-foreground')}
                                    >
                                        {/* A cursor over a frame: choosing one of
                                            yours, not bringing one in. Uploading a
                                            picture as inspiration is a different
                                            thing and belongs under "+".

                                            Icon alone. Four labelled chips plus
                                            Send do not fit across 380px, and the
                                            one that overflowed was Send — the
                                            button the composer exists for. The
                                            tooltip still says what this does. */}
                                        <SquareMousePointer className="size-3.5" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {t('CONTENT_STUDIO.PLANNER.PICK_RESULT_HINT',
                                        'Point at an image already on the canvas so the agent knows which one you mean')}
                                </TooltipContent>
                            </Tooltip>
                        )}

                        {/* Ask or Auto — whether a finished plan waits to be
                            read or simply happens. Its own surface rather than a
                            menu component, because the outside-click and Escape
                            handling above is what closes it. */}
                        <div ref={autonomyMenuRef} className="relative">
                            <Button
                                variant="outline"
                                onClick={() => setAutonomyOpen((v) => !v)}
                                aria-expanded={autonomyOpen}
                                className={cn(TOOLBAR_CHIP, autonomy === 'auto'
                                    ? 'border-ring bg-muted text-foreground'
                                    : 'text-muted-foreground')}
                            >
                                <Sparkles className="size-3.5" />
                                {autonomy === 'auto'
                                    ? t('CONTENT_STUDIO.PLANNER.AUTONOMY_AUTO', 'Auto')
                                    : t('CONTENT_STUDIO.PLANNER.AUTONOMY_ASK', 'Ask first')}
                            </Button>

                            {autonomyOpen && (
                                <div
                                    // Anchored to its end edge: the button sits
                                    // near the end of the toolbar, so opening
                                    // outwards would run it off the screen.
                                    // Overflowing the panel is fine — it draws
                                    // over the canvas, as a menu should.
                                    className="absolute bottom-full end-0 z-40 mb-1.5 w-[268px] max-w-[calc(100vw-48px)] overflow-hidden rounded-xl border bg-card shadow-sm"
                                >
                                    {([
                                        {
                                            key: 'ask' as const,
                                            title: t('CONTENT_STUDIO.PLANNER.AUTONOMY_ASK', 'Ask first'),
                                            hint: t('CONTENT_STUDIO.PLANNER.AUTONOMY_ASK_HINT',
                                                'Shows the plan and waits for you to approve it'),
                                        },
                                        {
                                            key: 'auto' as const,
                                            title: t('CONTENT_STUDIO.PLANNER.AUTONOMY_AUTO', 'Auto'),
                                            hint: t('CONTENT_STUDIO.PLANNER.AUTONOMY_AUTO_HINT',
                                                'Gets on with it and spends the credits — no approval step'),
                                        },
                                    ]).map((option) => (
                                        <button
                                            key={option.key}
                                            type="button"
                                            onClick={() => chooseAutonomy(option.key)}
                                            className="flex w-full items-start gap-2 px-3 py-2.5 text-start hover:bg-muted"
                                        >
                                            <span className="mt-0.5 w-3.5 shrink-0 text-foreground">
                                                {autonomy === option.key && <Check className="size-3.5" />}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-sm font-medium text-foreground">
                                                    {option.title}
                                                </span>
                                                <span className="block text-[11px] leading-snug text-muted-foreground">
                                                    {option.hint}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex-1" />

                        {running ? (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="icon-sm"
                                        onClick={stop}
                                        aria-label={t('CONTENT_STUDIO.PLANNER.STOP', 'Stop') as string}
                                    >
                                        <Square className="size-2.5 fill-current" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>{t('CONTENT_STUDIO.PLANNER.STOP', 'Stop')}</TooltipContent>
                            </Tooltip>
                        ) : (
                            <SendButton
                                enabled={!!message.trim()}
                                onClick={() => void send(message)}
                                ariaLabel={t('CONTENT_STUDIO.PLANNER.SEND', 'Send') as string}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════

/** Body and label, the panel's two text sizes, so they read the same everywhere. */
const PANEL_BODY = 'text-sm leading-normal text-foreground';
const PANEL_LABEL = 'text-xs text-muted-foreground';

/** The small outlined controls along the composer's toolbar. */
const TOOLBAR_CHIP = 'h-7 gap-1.5 px-2 text-[11px] font-medium';

/**
 * One thing attached to the message being written.
 *
 * A reference picture, a product and a node pointed at from the canvas all
 * render the same chip — thumbnail, name, and a way out — so it is written
 * once rather than three times.
 */
const ComposerChip: React.FC<{
    thumbnailUrl?: string;
    label: string;
    onRemove: () => void;
    /** Still uploading: the spinner sits where the picture will go, so the
     *  row does not jump when the url arrives. */
    busy?: boolean;
    /** Inspiration the vendor brought in, rather than something already here. */
    dashed?: boolean;
}> = ({ thumbnailUrl, label, onRemove, busy, dashed }) => (
    <span
        className={cn(
            'inline-flex max-w-[200px] items-center gap-1.5 rounded-md border bg-muted px-1 py-0.5',
            dashed && 'border-dashed',
        )}
    >
        {busy ? (
            <Loader2 className="size-[22px] shrink-0 animate-spin p-1 text-muted-foreground" />
        ) : thumbnailUrl ? (
            <img src={thumbnailUrl} alt="" className="size-[22px] shrink-0 rounded-full object-cover" />
        ) : null}
        <span className="min-w-0 truncate text-[11px] text-foreground">{label}</span>
        <button
            type="button"
            onClick={onRemove}
            className="shrink-0 px-0.5 leading-none text-muted-foreground hover:text-foreground"
        >
            <X className="size-3" />
        </button>
    </span>
);

const SendButton: React.FC<{
    enabled: boolean;
    onClick: () => void;
    ariaLabel: string;
}> = ({ enabled, onClick, ariaLabel }) => (
    <Button
        size="icon"
        onClick={onClick}
        disabled={!enabled}
        aria-label={ariaLabel}
        // The one round thing in a square panel, because it is the button the
        // composer exists for.
        className="size-[30px] shrink-0 rounded-full"
    >
        <ArrowUp className="size-3.5" />
    </Button>
);

/** "4 minutes ago" — enough precision for a chat list, no dependency needed. */
function relativeTime(iso: string, t: any): string {
    const then = Date.parse(iso);
    if (!Number.isFinite(then)) return '';
    const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (mins < 1) return t('CONTENT_STUDIO.PLANNER.JUST_NOW', 'just now');
    if (mins < 60) return t('CONTENT_STUDIO.PLANNER.MINUTES_AGO', { defaultValue: '{{count}} minutes ago', count: mins });
    const hours = Math.round(mins / 60);
    if (hours < 24) return t('CONTENT_STUDIO.PLANNER.HOURS_AGO', { defaultValue: '{{count}} hours ago', count: hours });
    const days = Math.round(hours / 24);
    return t('CONTENT_STUDIO.PLANNER.DAYS_AGO', { defaultValue: '{{count}} days ago', count: days });
}
